import type { FC, ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import type { Components, ExtraProps } from "react-markdown";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import remarkGfm from "remark-gfm";
import { Box, Typography } from "@mui/material";
import type { AssistantCardChoice } from "./AssistantChoicesCard";
import { AssistantChoicesCard, AssistantChoicesCardSkeleton } from "./AssistantChoicesCard";
import {
  AssistantCallout,
  AssistantComponentSkeleton,
  AssistantQuestionReference,
  AssistantRecordCard,
  AssistantRecordCardSkeleton,
  AssistantRecordCards,
  AssistantRecordLink,
} from "./AssistantMarkdownComponents";

const COMPONENT_ATTRIBUTES: Record<string, Array<string>> = {
  recordlink: ["formtype", "id"],
  recordcards: [],
  recordcard: ["formtype", "id", "status", "owner"],
  question: ["formtype", "id"],
  callout: ["type"],
  choices: ["multiple"],
  choice: ["label", "description"],
  pending: ["shape"],
};

const EXTERNAL_RESOURCE_TAGS = ["img", "picture", "source", "video", "audio", "iframe"];
const FOCUS_ATTRIBUTES = ["tabIndex", "accessKey"];
const CLOBBER_PREFIX = defaultSchema.clobberPrefix ?? "user-content-";

const SCHEMA = {
  ...defaultSchema,
  tagNames: [
    ...(defaultSchema.tagNames ?? []).filter(tagName => !EXTERNAL_RESOURCE_TAGS.includes(tagName)),
    ...Object.keys(COMPONENT_ATTRIBUTES),
  ],
  attributes: {
    ...defaultSchema.attributes,
    "*": (defaultSchema.attributes?.["*"] ?? []).filter(
      attribute => typeof attribute !== "string" || !FOCUS_ATTRIBUTES.includes(attribute)
    ),
    ...COMPONENT_ATTRIBUTES,
  },
};

const COMPONENT_NAMES = ["RecordLink", "RecordCards", "RecordCard", "Question", "Callout", "Choices", "Choice"];

const SELF_CLOSING_COMPONENT = new RegExp(`<(${COMPONENT_NAMES.join("|")})(\\s[^<>]*?)?\\s*\\/>`, "g");
const CHOICES_CARD_START = /\n*(<Choices\b)/g;
const CHOICES_CARD = /<Choices\b[\s\S]*?(?:<\/Choices>|$)/g;
const UNFINISHED_TAG = /<(\/?)([A-Za-z]*)([^<>]*)$/;

const withComponentsClosed = (text: string) =>
  text
    .replace(SELF_CLOSING_COMPONENT, (_, name: string, attributes = "") => `<${name}${attributes}></${name}>`)
    .replace(CHOICES_CARD_START, "\n\n$1");

const withChoicesCardsPending = (text: string) => text.replace(CHOICES_CARD, '<pending shape="choices"></pending>');

const DRAWN_WHEN_COMPLETE = /<(\/?)(RecordLink|RecordCard|Question)\b[^<>]*?(\/?)>/g;

const pendingFor = (tag: string) =>
  tag.startsWith("<RecordC")
    ? '<pending shape="card"></pending>'
    : tag.startsWith("<Choi")
      ? '<pending shape="choices"></pending>'
      : "<pending></pending>";

const unfinishedComponentStart = (text: string) => {
  let start: { index: number; name: string } | undefined;

  for (const match of text.matchAll(DRAWN_WHEN_COMPLETE)) {
    const [, closing, name, selfClosing] = match;

    if (closing && start?.name === name) start = undefined;
    else if (!closing && !selfClosing) start = { index: match.index, name };
  }

  return start;
};

const isUnfinishedComponentTag = (name: string, rest: string) =>
  rest.length === 0
    ? COMPONENT_NAMES.some(componentName => componentName.startsWith(name))
    : COMPONENT_NAMES.includes(name) && /^\s/.test(rest);

const withUnfinishedTagCut = (text: string) => {
  const match = UNFINISHED_TAG.exec(text);
  if (!match) return text;

  const [tag, closing, name, rest] = match;
  if (!isUnfinishedComponentTag(name, rest)) return text;

  return text.slice(0, match.index) + (closing ? "" : pendingFor(tag));
};

const withUnfinishedTagAsPending = (text: string) => {
  const unfinished = unfinishedComponentStart(text);

  return unfinished
    ? text.slice(0, unfinished.index) + pendingFor(text.slice(unfinished.index))
    : withUnfinishedTagCut(text);
};

const BOLD_MARKER = "**";

const withUnfinishedBoldCut = (text: string) =>
  text.split(BOLD_MARKER).length % 2 === 0 ? text.slice(0, text.lastIndexOf(BOLD_MARKER)) : text;

const withoutClobberPrefix = (id: string | undefined) =>
  id?.startsWith(CLOBBER_PREFIX) ? id.slice(CLOBBER_PREFIX.length) : id;

type Element = NonNullable<ExtraProps["node"]>;

const CHOICE_TAG = "choice";

const choiceText = (value: unknown) => (typeof value === "string" && value.trim().length > 0 ? value : undefined);

const choiceElements = (node: Element | undefined) =>
  (node?.children ?? []).filter((child): child is Element => child.type === "element" && child.tagName === CHOICE_TAG);

const toCardChoice = (element: Element): AssistantCardChoice => ({
  label: choiceText(element.properties.label) ?? "",
  description: choiceText(element.properties.description),
});

type ComponentProps = {
  formtype?: string;
  id?: string;
  type?: string;
  status?: string;
  owner?: string;
  children?: ReactNode;
};

const COMPONENTS = {
  p: ({ children }) => (
    <Typography component="div" sx={{ overflowWrap: "anywhere" }}>
      {children}
    </Typography>
  ),
  ul: ({ children }) => (
    <Box component="ul" sx={{ m: 0, pl: 3, display: "flex", flexDirection: "column", gap: 0.5 }}>
      {children}
    </Box>
  ),
  ol: ({ children }) => (
    <Box component="ol" sx={{ m: 0, pl: 3, display: "flex", flexDirection: "column", gap: 0.5 }}>
      {children}
    </Box>
  ),
  li: ({ children }) => (
    <Typography component="li" sx={{ "overflowWrap": "anywhere", "& > p": { display: "inline" } }}>
      {children}
    </Typography>
  ),
  h1: ({ children }) => (
    <Typography role="heading" aria-level={2} sx={{ fontWeight: 600, fontSize: theme => theme.typography.pxToRem(16) }}>
      {children}
    </Typography>
  ),
  h2: ({ children }) => (
    <Typography role="heading" aria-level={3} sx={{ fontWeight: 600, fontSize: theme => theme.typography.pxToRem(16) }}>
      {children}
    </Typography>
  ),
  h3: ({ children }) => (
    <Typography role="heading" aria-level={4} sx={{ fontWeight: 600 }}>
      {children}
    </Typography>
  ),
  table: ({ children }) => (
    <Box sx={{ overflowX: "auto" }}>
      <Box
        component="table"
        sx={{
          "borderCollapse": "collapse",
          "fontSize": theme => theme.typography.pxToRem(14),
          "& th, & td": {
            textAlign: "left",
            verticalAlign: "top",
            px: 1,
            py: 0.5,
            borderBottom: theme => `1px solid ${theme.palette.divider}`,
          },
          "& th": { fontWeight: 600 },
        }}
      >
        {children}
      </Box>
    </Box>
  ),
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
  recordlink: ({ formtype, id, children }: ComponentProps) => {
    const recordId = withoutClobberPrefix(id);
    return recordId ? (
      <AssistantRecordLink formType={formtype ?? ""} recordId={recordId} name={children} />
    ) : (
      <>{children}</>
    );
  },
  question: ({ formtype, id }: ComponentProps) => {
    const questionId = withoutClobberPrefix(id);
    return questionId ? <AssistantQuestionReference formType={formtype ?? ""} questionId={questionId} /> : null;
  },
  recordcards: ({ children }: ComponentProps) => <AssistantRecordCards>{children}</AssistantRecordCards>,
  recordcard: ({ formtype, id, status, owner, children }: ComponentProps) => {
    const recordId = withoutClobberPrefix(id);
    return recordId ? (
      <AssistantRecordCard
        formType={formtype ?? ""}
        recordId={recordId}
        name={children}
        status={status}
        owner={owner}
      />
    ) : null;
  },
  choices: ({ node, multiple }: { node?: Element; multiple?: boolean | string }) => {
    const choices = choiceElements(node)
      .map(toCardChoice)
      .filter(choice => choice.label.length > 0);
    return choices.length > 0 ? (
      <AssistantChoicesCard choices={choices} allowMultiple={multiple === true || multiple === "true"} />
    ) : null;
  },
  choice: () => null,
  pending: ({ shape }: { shape?: string }) =>
    shape === "card" ? (
      <AssistantRecordCardSkeleton />
    ) : shape === "choices" ? (
      <AssistantChoicesCardSkeleton />
    ) : (
      <AssistantComponentSkeleton />
    ),
  callout: ({ type, children }: ComponentProps) => (
    <AssistantCallout warning={type === "warning"}>{children}</AssistantCallout>
  ),
} as Components;

export const AssistantMarkdown: FC<{ text: string; isStreaming?: boolean }> = ({ text, isStreaming = false }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeRaw, [rehypeSanitize, SCHEMA]]}
      components={COMPONENTS}
    >
      {isStreaming
        ? withUnfinishedTagAsPending(withComponentsClosed(withChoicesCardsPending(withUnfinishedBoldCut(text))))
        : withComponentsClosed(text)}
    </ReactMarkdown>
  </Box>
);
